import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from './cn.js';

const GAP = 8;
const MARGIN = 8;

// Anchor point on the trigger and how the bubble hangs off it, per side.
const placements = {
  top: (r) => ({ x: r.left + r.width / 2, y: r.top - GAP, tx: '-50%', ty: '-100%' }),
  bottom: (r) => ({ x: r.left + r.width / 2, y: r.bottom + GAP, tx: '-50%', ty: '0' }),
  left: (r) => ({ x: r.left - GAP, y: r.top + r.height / 2, tx: '-100%', ty: '-50%' }),
  right: (r) => ({ x: r.right + GAP, y: r.top + r.height / 2, tx: '0', ty: '-50%' }),
};

/**
 * Lightweight tooltip. Wrap any element; `content` shows on hover/focus.
 * The bubble is portaled to `body` with fixed positioning, so scroll
 * containers (overflow) and stacking contexts never clip or cover it; it is
 * nudged back inside the viewport when the preferred side has no room.
 * For long or interactive content use a Popover instead.
 */
export default function Tooltip({ content, side = 'top', className, children, delay = false }) {
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const timer = useRef(0);
  const [anchor, setAnchor] = useState(null);
  const [shift, setShift] = useState({ x: 0, y: 0 });

  const show = useCallback(() => {
    clearTimeout(timer.current);
    const open = () => {
      const element = triggerRef.current;
      if (!element) return;
      setShift({ x: 0, y: 0 });
      setAnchor((placements[side] || placements.top)(element.getBoundingClientRect()));
    };
    if (delay) timer.current = setTimeout(open, 300);
    else open();
  }, [side, delay]);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setAnchor(null);
  }, []);

  // Keep the bubble inside the viewport.
  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;
    const rect = bubble.getBoundingClientRect();
    let x = 0;
    let y = 0;
    if (rect.left < MARGIN) x = MARGIN - rect.left;
    else if (rect.right > window.innerWidth - MARGIN) x = window.innerWidth - MARGIN - rect.right;
    if (rect.top < MARGIN) y = MARGIN - rect.top;
    else if (rect.bottom > window.innerHeight - MARGIN) y = window.innerHeight - MARGIN - rect.bottom;
    if (x || y) setShift({ x, y });
  }, [anchor]);

  // A scrolling ancestor would move the trigger away from the bubble.
  useEffect(() => {
    if (!anchor) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [anchor, hide]);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!content) return children;

  return (
    <span
      ref={triggerRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {anchor &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            ref={bubbleRef}
            role="tooltip"
            className="pointer-events-none fixed left-0 top-0 z-[200] whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-pop animate-fade-in"
            style={{
              transform: `translate(${anchor.x + shift.x}px, ${anchor.y + shift.y}px) translate(${anchor.tx}, ${anchor.ty})`,
            }}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
